import { appConfig } from "@/shared/config";
import { EventStream, invalidateJWT, isRecord, type ServerEvent } from "@/shared/http";

/**
 * 앱 셸의 변경 스트림(`/v1/events`)이 구독자에게 주는 신호. 매핑과 Dataset 조사 사실이 같은 스트림을 쓴다.
 *
 * - `changed` — 매핑 저장 · 삭제(`mapping.changed` · `mapping.deleted`).
 * - `facts_changed` — Dataset 조사 사실의 제안 · 확정 · 폐기(`dataset_facts.changed`). 구독자는 datasets 모듈의
 *   사실 편집 상태다. inspect 는 datasets 를 모르므로 신호만 나른다.
 * - `ready` · `offline` · `reconcile` — 연결 상태와 재대조 요청. 두 구독자 모두 받는다.
 */
export type MappingChange =
  | { readonly kind: "ready" | "offline" | "reconcile" }
  | { readonly kind: "changed"; readonly datasetUuid: string; readonly revisionUuid: string }
  | { readonly kind: "facts_changed"; readonly datasetUuid: string };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** 변경 힌트에는 식별자만 받는다. 매핑과 사실의 실제 값은 항상 GET으로 확인한다. */
export function parseMappingChange(message: ServerEvent): MappingChange | null {
  if (message.event === "ready") return { kind: "ready" };
  if (message.event === "dataset_facts.changed") return parseFactsChange(message.data);
  if (message.event !== "mapping.changed" && message.event !== "mapping.deleted") return null;
  const value = parseJson(message.data);
  if (
    !isRecord(value) ||
    typeof value.dataset_uuid !== "string" ||
    typeof value.revision_uuid !== "string" ||
    !UUID.test(value.dataset_uuid) ||
    !UUID.test(value.revision_uuid)
  )
    return null;
  return { kind: "changed", datasetUuid: value.dataset_uuid, revisionUuid: value.revision_uuid };
}

function parseFactsChange(data: string): MappingChange | null {
  const value = parseJson(data);
  if (!isRecord(value) || typeof value.dataset_uuid !== "string" || !UUID.test(value.dataset_uuid))
    return null;
  return { kind: "facts_changed", datasetUuid: value.dataset_uuid };
}

function parseJson(data: string): unknown {
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

/** 탭의 로그인 세션 하나가 스트림을 소유한다. 창들은 구독만 하고 REST 쓰기는 별도로 수행한다. */
class MappingChanges {
  private stream: EventStream | null = null;
  private listeners = new Set<(change: MappingChange) => void>();
  private status: "idle" | "connecting" | "ready" | "offline" = "idle";

  get connecting(): boolean {
    return this.status === "connecting";
  }

  start(token: string): void {
    this.stop();
    this.status = "connecting";
    this.stream = new EventStream({
      url: `${appConfig.apiBaseUrl}/v1/events`,
      token,
      onEvent: (event) => {
        if (event.event === "session.expired") {
          this.stop();
          invalidateJWT(token);
          return;
        }
        const change = parseMappingChange(event);
        if (change !== null) {
          if (change.kind === "ready") this.status = "ready";
          this.emit(change);
        }
      },
      onDisconnected: () => {
        this.status = "offline";
        this.emit({ kind: "offline" });
      },
      onUnauthorized: () => {
        this.stop();
        invalidateJWT(token);
      },
    });
    this.stream.init();
  }

  stop(): void {
    this.stream?.dispose();
    this.stream = null;
    this.status = "idle";
  }

  reconcile(): void {
    this.emit({ kind: "reconcile" });
  }

  subscribe(listener: (change: MappingChange) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private emit(change: MappingChange): void {
    for (const listener of this.listeners) listener(change);
  }
}

export const mappingChanges = new MappingChanges();
